/**
 * Post-match end screen — shows match results, MVP, stats table, and rating change.
 * Industrial/utilitarian FPS terminal aesthetic with CRT scan-line effect and
 * staggered stat reveals.
 */

import { escapeHtml } from '../utils';

const ACCENT = '#e94560';
const BG_DARK = '#1a1a2e';
const BG_PANEL = '#16213e';
const T_COLOR = '#e8b849';
const CT_COLOR = '#5b9bd5';

/** Inject keyframe animations once into the document head. */
let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes cp-end-fadein {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes cp-end-scorepop {
      0%   { transform: scale(0.6); opacity: 0; }
      60%  { transform: scale(1.08); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes cp-end-glow {
      0%, 100% { box-shadow: 0 0 8px ${ACCENT}40, inset 0 0 8px ${ACCENT}10; }
      50%      { box-shadow: 0 0 18px ${ACCENT}80, inset 0 0 12px ${ACCENT}20; }
    }
    @keyframes cp-end-scanline {
      0%   { transform: translateY(-100%); }
      100% { transform: translateY(100vh); }
    }
    @keyframes cp-end-rowslide {
      from { opacity: 0; transform: translateX(-10px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    .cp-end-row:hover {
      background: rgba(233,69,96,0.08) !important;
    }
  `;
  document.head.appendChild(style);
}

export interface MatchPlayerStats {
  name: string;
  team: 'T' | 'CT';
  kills: number;
  deaths: number;
  assists: number;
  damage: number;
  moneySpent: number;
  isLocal: boolean;
}

export interface MatchMvpHighlight {
  name: string;
  bestFragCount: number;
}

export interface MatchData {
  winnerTeam: 'T' | 'CT';
  tScore: number;
  ctScore: number;
  players: MatchPlayerStats[];
  ratingChange?: number; // positive or negative
  mvp?: MatchMvpHighlight;
}

// Singleton module — one end screen overlay shared across the app.
// Module-level mutable state is intentional for this UI singleton pattern.
let overlay: HTMLElement | null = null;
let playAgainCallback: (() => void) | null = null;
let menuCallback: (() => void) | null = null;
let escapeHandler: ((e: KeyboardEvent) => void) | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  injectStyles();

  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.92)',
    zIndex: '80',
    display: 'none',
    fontFamily: '"Courier New", monospace',
    color: '#ffffff',
    userSelect: 'none',
    overflowY: 'auto',
  });

  // Scan-line overlay — subtle CRT effect
  const scanline = document.createElement('div');
  Object.assign(scanline.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '4px',
    background: 'rgba(233,69,96,0.03)',
    zIndex: '81',
    pointerEvents: 'none',
    animation: 'cp-end-scanline 3s linear infinite',
  });
  overlay.appendChild(scanline);

  document.body.appendChild(overlay);
  return overlay;
}

export function showEndScreen(matchData: MatchData): void {
  const el = ensureOverlay();
  // Preserve the scan-line (first child) across re-shows; remove everything else.
  while (el.childNodes.length > 1) {
    el.removeChild(el.lastChild!);
  }
  el.style.display = 'block';

  // Bind Escape to dismiss — matches behaviour of other major overlays.
  if (!escapeHandler) {
    escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hideEndScreen();
      }
    };
    document.addEventListener('keydown', escapeHandler);
  }

  const wrapper = document.createElement('div');
  Object.assign(wrapper.style, {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '40px 20px',
  });

  // -- MATCH OVER header --
  const header = document.createElement('div');
  Object.assign(header.style, {
    textAlign: 'center',
    marginBottom: '24px',
  });

  const matchOver = document.createElement('div');
  Object.assign(matchOver.style, {
    fontSize: '36px',
    fontWeight: 'bold',
    color: ACCENT,
    letterSpacing: '8px',
    textTransform: 'uppercase',
    textShadow: `0 0 20px ${ACCENT}, 0 0 40px ${ACCENT}40`,
    marginBottom: '16px',
    animation: 'cp-end-scorepop 0.5s ease-out both',
  });
  matchOver.textContent = 'MATCH OVER';
  header.appendChild(matchOver);

  // Winning team
  const winColor = matchData.winnerTeam === 'T' ? T_COLOR : CT_COLOR;
  const winLabel = matchData.winnerTeam === 'T' ? 'TERRORISTS WIN' : 'COUNTER-TERRORISTS WIN';
  const winnerEl = document.createElement('div');
  Object.assign(winnerEl.style, {
    fontSize: '22px',
    fontWeight: 'bold',
    color: winColor,
    letterSpacing: '4px',
    marginBottom: '12px',
    textShadow: `0 0 12px ${winColor}, 0 0 30px ${winColor}30`,
    animation: 'cp-end-fadein 0.4s ease-out 0.3s both',
  });
  winnerEl.textContent = winLabel;
  header.appendChild(winnerEl);

  // Score
  const scoreEl = document.createElement('div');
  Object.assign(scoreEl.style, {
    fontSize: '28px',
    fontWeight: 'bold',
    letterSpacing: '2px',
    animation: 'cp-end-scorepop 0.5s ease-out 0.5s both',
  });
  const tLabel = document.createElement('span');
  tLabel.style.color = T_COLOR;
  tLabel.textContent = 'T';
  scoreEl.appendChild(tLabel);
  scoreEl.appendChild(document.createTextNode(' '));
  const tScoreSpan = document.createElement('span');
  tScoreSpan.style.fontSize = '36px';
  tScoreSpan.textContent = String(matchData.tScore);
  scoreEl.appendChild(tScoreSpan);
  scoreEl.appendChild(document.createTextNode(' '));
  const colonSpan = document.createElement('span');
  Object.assign(colonSpan.style, { color: '#555', margin: '0 8px' });
  colonSpan.textContent = ':';
  scoreEl.appendChild(colonSpan);
  scoreEl.appendChild(document.createTextNode(' '));
  const ctScoreSpan = document.createElement('span');
  ctScoreSpan.style.fontSize = '36px';
  ctScoreSpan.textContent = String(matchData.ctScore);
  scoreEl.appendChild(ctScoreSpan);
  scoreEl.appendChild(document.createTextNode(' '));
  const ctLabel = document.createElement('span');
  ctLabel.style.color = CT_COLOR;
  ctLabel.textContent = 'CT';
  scoreEl.appendChild(ctLabel);
  header.appendChild(scoreEl);

  // Rating change
  if (matchData.ratingChange !== undefined) {
    const ratingEl = document.createElement('div');
    const isPositive = matchData.ratingChange >= 0;
    Object.assign(ratingEl.style, {
      fontSize: '18px',
      marginTop: '14px',
      color: isPositive ? '#44ff44' : '#ff4444',
      fontWeight: 'bold',
      letterSpacing: '1px',
    });
    ratingEl.textContent = `RATING: ${isPositive ? '+' : ''}${matchData.ratingChange}`;
    header.appendChild(ratingEl);
  }

  wrapper.appendChild(header);

  // -- MVP --
  // Sort by kills desc, then deaths asc (fewer is better), then assists desc
  const sorted = [...matchData.players].sort(
    (a, b) => b.kills - a.kills || a.deaths - b.deaths || b.assists - a.assists,
  );
  const computedMvp = sorted[0];
  if (computedMvp) {
    const mvpEl = document.createElement('div');
    Object.assign(mvpEl.style, {
      textAlign: 'center',
      padding: '16px 24px',
      marginBottom: '24px',
      background: `linear-gradient(135deg, ${BG_PANEL}, ${BG_DARK})`,
      border: `1px solid ${ACCENT}`,
      borderRadius: '4px',
      animation: 'cp-end-glow 2s ease-in-out infinite, cp-end-fadein 0.4s ease-out 0.7s both',
    });
    const mvpTeamColor = computedMvp.team === 'T' ? T_COLOR : CT_COLOR;

    const mvpLabel = document.createElement('div');
    Object.assign(mvpLabel.style, { fontSize: '11px', color: '#999', letterSpacing: '3px', marginBottom: '6px' });
    mvpLabel.textContent = 'MATCH MVP';
    mvpEl.appendChild(mvpLabel);

    const mvpNameRow = document.createElement('div');
    Object.assign(mvpNameRow.style, { fontSize: '20px', fontWeight: 'bold', color: ACCENT });
    const mvpNameSpan = document.createTextNode(computedMvp.name);
    mvpNameRow.appendChild(mvpNameSpan);
    const mvpTeamSpan = document.createElement('span');
    Object.assign(mvpTeamSpan.style, { color: mvpTeamColor, fontSize: '12px', marginLeft: '8px' });
    mvpTeamSpan.textContent = `[${computedMvp.team}]`;
    mvpNameRow.appendChild(mvpTeamSpan);
    mvpEl.appendChild(mvpNameRow);

    const mvpStatsRow = document.createElement('div');
    Object.assign(mvpStatsRow.style, { fontSize: '13px', color: '#aaa', marginTop: '4px' });
    mvpStatsRow.textContent = `${computedMvp.kills} kills / ${computedMvp.deaths} deaths / ${computedMvp.assists} assists`;
    mvpEl.appendChild(mvpStatsRow);

    wrapper.appendChild(mvpEl);
  }

  if (matchData.mvp) {
    const highlightEl = document.createElement('div');
    Object.assign(highlightEl.style, {
      padding: '14px 18px',
      marginBottom: '24px',
      background: `linear-gradient(135deg, ${BG_PANEL}, ${BG_DARK})`,
      border: `1px solid ${T_COLOR}60`,
      borderRadius: '4px',
      animation: 'cp-end-fadein 0.4s ease-out 0.85s both',
    });

    const highlightLabel = document.createElement('div');
    Object.assign(highlightLabel.style, {
      fontSize: '11px',
      color: T_COLOR,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      marginBottom: '8px',
      fontWeight: 'bold',
    });
    highlightLabel.textContent = 'MVP HIGHLIGHT';
    highlightEl.appendChild(highlightLabel);

    const highlightRow = document.createElement('div');
    Object.assign(highlightRow.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap',
    });

    const highlightCopy = document.createElement('div');
    highlightCopy.style.flex = '1';
    const highlightName = document.createElement('div');
    Object.assign(highlightName.style, { fontSize: '17px', color: '#fff', fontWeight: 'bold' });
    highlightName.textContent = matchData.mvp.name;
    highlightCopy.appendChild(highlightName);

    const highlightStats = document.createElement('div');
    Object.assign(highlightStats.style, { fontSize: '12px', color: '#aaa', marginTop: '3px' });
    highlightStats.textContent = `Best frag count: ${matchData.mvp.bestFragCount}`;
    highlightCopy.appendChild(highlightStats);
    highlightRow.appendChild(highlightCopy);

    const reelBtn = createButton('HIGHLIGHT REEL COMING SOON', '#666', () => {});
    reelBtn.style.pointerEvents = 'none';
    reelBtn.style.opacity = '0.65';
    reelBtn.setAttribute('aria-disabled', 'true');
    highlightRow.appendChild(reelBtn);

    highlightEl.appendChild(highlightRow);
    wrapper.appendChild(highlightEl);
  }

  // -- Stats table --
  const tableWrap = document.createElement('div');
  Object.assign(tableWrap.style, {
    marginBottom: '30px',
    overflowX: 'auto',
  });

  const table = document.createElement('table');
  Object.assign(table.style, {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    fontFamily: '"Courier New", monospace',
  });

  // Header row
  const thead = document.createElement('thead');
  const hRow = document.createElement('tr');
  Object.assign(hRow.style, {
    borderBottom: `2px solid ${ACCENT}`,
  });
  const headers = ['Player', 'K', 'D', 'A', 'Damage', 'Spent'];
  const aligns = ['left', 'center', 'center', 'center', 'center', 'center'];
  for (let i = 0; i < headers.length; i++) {
    const th = document.createElement('th');
    Object.assign(th.style, {
      textAlign: aligns[i],
      padding: '8px 6px',
      color: '#888',
      fontWeight: 'bold',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontSize: '11px',
    });
    th.textContent = headers[i];
    hRow.appendChild(th);
  }
  thead.appendChild(hRow);
  table.appendChild(thead);

  // Body — T team first, then CT
  const tbody = document.createElement('tbody');

  const teams: Array<'T' | 'CT'> = ['T', 'CT'];
  for (const team of teams) {
    const teamPlayers = sorted.filter((p) => p.team === team);
    if (teamPlayers.length === 0) continue;

    // Team separator
    const sepRow = document.createElement('tr');
    const sepTd = document.createElement('td');
    sepTd.colSpan = 6;
    const teamColor = team === 'T' ? T_COLOR : CT_COLOR;
    Object.assign(sepTd.style, {
      padding: '6px',
      fontSize: '11px',
      fontWeight: 'bold',
      color: teamColor,
      letterSpacing: '2px',
      borderBottom: `1px solid ${teamColor}`,
    });
    sepTd.textContent = team === 'T' ? 'TERRORISTS' : 'COUNTER-TERRORISTS';
    sepRow.appendChild(sepTd);
    tbody.appendChild(sepRow);

    let rowIdx = 0;
    for (const p of teamPlayers) {
      const row = document.createElement('tr');
      row.className = 'cp-end-row';
      const delay = 0.9 + rowIdx * 0.06; // staggered entrance
      Object.assign(row.style, {
        borderBottom: '1px solid #222',
        background: p.isLocal ? 'rgba(233,69,96,0.12)' : 'transparent',
        animation: `cp-end-rowslide 0.3s ease-out ${delay}s both`,
        transition: 'background 0.15s',
        cursor: 'default',
      });

      // K/D ratio for color coding
      const kd = p.deaths > 0 ? p.kills / p.deaths : p.kills;
      const kdColor = kd >= 1.5 ? '#44ff44' : kd >= 1.0 ? '#aaffaa' : kd >= 0.5 ? '#ffaa44' : '#ff4444';

      const cells = [
        escapeHtml(p.name),
        String(p.kills),
        String(p.deaths),
        String(p.assists),
        String(p.damage),
        `$${p.moneySpent}`,
      ];

      for (let i = 0; i < cells.length; i++) {
        const td = document.createElement('td');
        let cellColor = '#ccc';
        if (i === 0 && p.isLocal) cellColor = ACCENT;
        else if (i === 1) cellColor = kdColor; // kills colored by K/D
        else if (i === 2 && p.deaths > 0) cellColor = '#ff6666'; // deaths always reddish

        Object.assign(td.style, {
          textAlign: aligns[i],
          padding: '8px 6px',
          color: cellColor,
          fontWeight: i === 0 || i === 1 ? 'bold' : 'normal',
        });
        td.textContent = cells[i];
        row.appendChild(td);
      }

      tbody.appendChild(row);
      rowIdx++;
    }
  }

  table.appendChild(tbody);
  tableWrap.appendChild(table);
  wrapper.appendChild(tableWrap);

  // -- Buttons --
  const btnRow = document.createElement('div');
  Object.assign(btnRow.style, {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    animation: 'cp-end-fadein 0.4s ease-out 1.6s both',
  });

  const playAgainBtn = createButton('PLAY AGAIN', ACCENT, () => {
    const cb = playAgainCallback;
    hideEndScreen();
    if (cb) cb();
  });
  btnRow.appendChild(playAgainBtn);

  const shareBtn = createButton('SHARE RESULT', '#666', () => {
    void shareMatchResult(matchData, el);
  });
  btnRow.appendChild(shareBtn);

  const menuBtn = createButton('RETURN TO MENU', '#444', () => {
    const cb = menuCallback;
    hideEndScreen();
    if (cb) cb();
  });
  btnRow.appendChild(menuBtn);

  wrapper.appendChild(btnRow);
  el.appendChild(wrapper);
}

export function hideEndScreen(): void {
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
  if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler);
    escapeHandler = null;
  }
  if (overlay) {
    overlay.style.display = 'none';
    overlay.innerHTML = '';
  }
}

export function onPlayAgain(callback: () => void): void {
  playAgainCallback = callback;
}

export function onReturnToMenu(callback: () => void): void {
  menuCallback = callback;
}

export function disposeEndScreen(): void {
  // Clear callbacks first so any in-flight click handlers become no-ops
  playAgainCallback = null;
  menuCallback = null;
  hideEndScreen();
  if (overlay) {
    overlay.remove();
    overlay = null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function createButton(text: string, bgColor: string, onClick: () => void): HTMLElement {
  const btn = document.createElement('div');
  const isAccent = bgColor === ACCENT;
  Object.assign(btn.style, {
    padding: '14px 36px',
    fontSize: '13px',
    fontWeight: 'bold',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#ffffff',
    background: isAccent ? bgColor : 'transparent',
    border: `2px solid ${bgColor}`,
    borderRadius: '2px',
    cursor: 'pointer',
    fontFamily: '"Courier New", monospace',
    transition: 'all 0.2s ease-out',
    textAlign: 'center',
    boxShadow: isAccent ? `0 0 12px ${ACCENT}30` : 'none',
  });

  btn.addEventListener('mouseenter', () => {
    btn.style.filter = 'brightness(1.3)';
    btn.style.transform = 'translateY(-2px)';
    btn.style.boxShadow = `0 4px 16px ${bgColor}40`;
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.filter = 'brightness(1)';
    btn.style.transform = 'translateY(0)';
    btn.style.boxShadow = isAccent ? `0 0 12px ${ACCENT}30` : 'none';
  });
  btn.addEventListener('click', onClick);
  btn.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
  });

  btn.setAttribute('role', 'button');
  btn.setAttribute('tabindex', '0');
  btn.textContent = text;
  return btn;
}

async function shareMatchResult(matchData: MatchData, parent: HTMLElement): Promise<void> {
  const topFrag = [...matchData.players].sort(
    (a, b) => b.kills - a.kills || a.deaths - b.deaths || b.assists - a.assists,
  )[0];
  const result = `Counter-Pump match: T ${matchData.tScore} - ${matchData.ctScore} CT - Top frag: ${topFrag?.name ?? 'Unknown'}`;

  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(result);
    }
  } finally {
    showCopiedToast(parent);
  }
}

function showCopiedToast(parent: HTMLElement): void {
  const toast = document.createElement('div');
  Object.assign(toast.style, {
    position: 'fixed',
    left: '50%',
    bottom: '34px',
    transform: 'translateX(-50%)',
    padding: '8px 14px',
    background: 'rgba(0,0,0,0.85)',
    border: `1px solid ${ACCENT}`,
    borderRadius: '3px',
    color: '#ffffff',
    fontFamily: '"Courier New", monospace',
    fontSize: '12px',
    fontWeight: 'bold',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    zIndex: '82',
    pointerEvents: 'none',
  });
  toast.textContent = 'Copied!';
  parent.appendChild(toast);

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.remove();
    toastTimer = null;
  }, 1500);
}
